import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ConversationViewDto,
  CopilotSessionDto,
  CopilotTurnDto,
  CopilotViewDto,
  DecideDraftBody,
  DecideDraftResultDto,
  DecisionDto,
  DriveMode,
  IntelPatch,
  LearnResultDto,
  MemberOutreachDto,
  MemberTagDto,
  OutreachConversationDto,
  OutreachConversationRowDto,
  OutreachIntelDto,
  OutreachOverviewDto,
  ProposalDto,
  SimScriptsDto,
  SimStepViewDto,
  StyleNoteDto,
  TurnOutcomeDto,
} from '../data-transfer-objects/outreach.dto';

/**
 * The admin API for outreach autodrive: Sharon's conversations, the copilot,
 * intel, and the QA simulator. Every route is behind the admin middleware;
 * errors come back as `{ error, ...details }` (see outreachError in the DTOs).
 *
 * Only HttpClient here, so providing it in root is safe (see app.config.ts).
 */
@Injectable({ providedIn: 'root' })
export class OutreachService {
  private readonly base = `${environment.baseURL}`;

  constructor(private http: HttpClient) {}

  overview(): Observable<OutreachOverviewDto> {
    return this.http.get<OutreachOverviewDto>(`${this.base}/outreach/overview`);
  }

  /* ------------------------------ copilot ------------------------------ */

  sessions(): Observable<{ sessions: CopilotSessionDto[] }> {
    return this.http.get<{ sessions: CopilotSessionDto[] }>(`${this.base}/outreach/copilot/sessions`);
  }

  createSession(title?: string): Observable<{ session: CopilotSessionDto }> {
    return this.http.post<{ session: CopilotSessionDto }>(`${this.base}/outreach/copilot/sessions`, title ? { title } : {});
  }

  session(id: number): Observable<CopilotViewDto> {
    return this.http.get<CopilotViewDto>(`${this.base}/outreach/copilot/sessions/${id}`);
  }

  /** Can take a couple of minutes: the copilot may run several tools. */
  sendToCopilot(id: number, text: string): Observable<CopilotTurnDto> {
    return this.http.post<CopilotTurnDto>(`${this.base}/outreach/copilot/sessions/${id}/messages`, { text });
  }

  proposals(status?: string): Observable<{ proposals: ProposalDto[] }> {
    const params = status ? new HttpParams().set('status', status) : undefined;
    return this.http.get<{ proposals: ProposalDto[] }>(`${this.base}/outreach/proposals`, { params });
  }

  /** `note` rewords an accept_style_note proposal before it is accepted. */
  approveProposal(id: number, note?: string): Observable<{ proposal: ProposalDto }> {
    return this.http.post<{ proposal: ProposalDto }>(
      `${this.base}/outreach/proposals/${id}/approve`,
      note ? { note } : {}
    );
  }

  rejectProposal(id: number, reason?: string): Observable<{ proposal: ProposalDto }> {
    return this.http.post<{ proposal: ProposalDto }>(
      `${this.base}/outreach/proposals/${id}/reject`,
      reason ? { reason } : {}
    );
  }

  decisions(): Observable<{ decisions: DecisionDto[] }> {
    return this.http.get<{ decisions: DecisionDto[] }>(`${this.base}/outreach/decisions`);
  }

  styleNotes(): Observable<{ notes: StyleNoteDto[] }> {
    return this.http.get<{ notes: StyleNoteDto[] }>(`${this.base}/outreach/style-notes`);
  }

  /* --------------------------- conversations --------------------------- */

  conversations(mode?: DriveMode): Observable<{ conversations: OutreachConversationRowDto[] }> {
    const params = mode ? new HttpParams().set('mode', mode) : undefined;
    return this.http.get<{ conversations: OutreachConversationRowDto[] }>(`${this.base}/outreach/conversations`, {
      params,
    });
  }

  conversation(id: number): Observable<ConversationViewDto> {
    return this.http.get<ConversationViewDto>(`${this.base}/outreach/conversations/${id}`);
  }

  /** 409 with `needsForce: true` when switching to auto on an untrusted cause. */
  setMode(id: number, to: DriveMode, note?: string, force = false): Observable<ConversationViewDto> {
    const body: { to: DriveMode; note?: string; force?: boolean } = { to };
    if (note) body.note = note;
    if (force) body.force = true;
    return this.http.post<ConversationViewDto>(`${this.base}/outreach/conversations/${id}/mode`, body);
  }

  draftNow(id: number): Observable<ConversationViewDto & { outcome: TurnOutcomeDto }> {
    return this.http.post<ConversationViewDto & { outcome: TurnOutcomeDto }>(
      `${this.base}/outreach/conversations/${id}/draft-now`,
      {}
    );
  }

  markSeen(id: number): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`${this.base}/outreach/conversations/${id}/seen`, {});
  }

  /** 422 with `problems` when the text fails the checks. */
  decideDraft(id: number, body: DecideDraftBody): Observable<DecideDraftResultDto> {
    return this.http.post<DecideDraftResultDto>(`${this.base}/outreach/drafts/${id}/decide`, body);
  }

  learnFromDraft(id: number): Observable<LearnResultDto> {
    return this.http.post<LearnResultDto>(`${this.base}/outreach/drafts/${id}/learn`, {});
  }

  /* ------------------------------- intel ------------------------------- */

  intel(filters: { status?: string; type?: string } = {}): Observable<{ intel: OutreachIntelDto[] }> {
    let params = new HttpParams();
    if (filters.status) params = params.set('status', filters.status);
    if (filters.type) params = params.set('type', filters.type);
    return this.http.get<{ intel: OutreachIntelDto[] }>(`${this.base}/outreach/intel`, { params });
  }

  updateIntel(id: number, patch: IntelPatch): Observable<{ intel: OutreachIntelDto }> {
    return this.http.patch<{ intel: OutreachIntelDto }>(`${this.base}/outreach/intel/${id}`, patch);
  }

  /* ------------------------------ members ------------------------------ */

  memberOutreach(userId: string): Observable<MemberOutreachDto> {
    return this.http.get<MemberOutreachDto>(`${this.base}/users/${userId}/outreach`);
  }

  /** 409 with the reason in words when the member cannot be approached. */
  startForMember(
    userId: string,
    cause: string,
    mode: 'assisted' | 'auto'
  ): Observable<{ conversation: OutreachConversationDto }> {
    return this.http.post<{ conversation: OutreachConversationDto }>(`${this.base}/users/${userId}/outreach/start`, {
      cause,
      mode,
    });
  }

  tags(userId: string): Observable<{ tags: MemberTagDto[] }> {
    return this.http.get<{ tags: MemberTagDto[] }>(`${this.base}/users/${userId}/tags`);
  }

  setTag(userId: string, tag: string, note?: string): Observable<{ tags: MemberTagDto[] }> {
    return this.http.put<{ tags: MemberTagDto[] }>(`${this.base}/users/${userId}/tags`, note ? { tag, note } : { tag });
  }

  removeTag(userId: string, tag: string): Observable<{ tags: MemberTagDto[] }> {
    return this.http.delete<{ tags: MemberTagDto[] }>(
      `${this.base}/users/${userId}/tags/${encodeURIComponent(tag)}`
    );
  }

  /* ----------------------------- simulator ----------------------------- */

  simScripts(): Observable<SimScriptsDto> {
    return this.http.get<SimScriptsDto>(`${this.base}/outreach/sim/scripts`);
  }

  simSessions(): Observable<{ conversations: OutreachConversationRowDto[] }> {
    return this.http.get<{ conversations: OutreachConversationRowDto[] }>(`${this.base}/outreach/sim/sessions`);
  }

  simCreate(body: { personaId: string; cause: string; mode: 'assisted' | 'auto' }): Observable<SimStepViewDto> {
    return this.http.post<SimStepViewDto>(`${this.base}/outreach/sim/sessions`, body);
  }

  simMemberMessage(id: number, text: string): Observable<SimStepViewDto> {
    return this.http.post<SimStepViewDto>(`${this.base}/outreach/sim/sessions/${id}/member-message`, { text });
  }

  simAdvance(id: number): Observable<SimStepViewDto> {
    return this.http.post<SimStepViewDto>(`${this.base}/outreach/sim/sessions/${id}/advance`, {});
  }

  simReset(id: number): Observable<SimStepViewDto> {
    return this.http.post<SimStepViewDto>(`${this.base}/outreach/sim/sessions/${id}/reset`, {});
  }

  simDelete(id: number): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(`${this.base}/outreach/sim/sessions/${id}`);
  }
}
